import { ReactElement, useCallback, useMemo, useState } from 'react'
import { documentationRootGuid } from '@constants'
import { find, flatten, isUndefined, sortBy } from 'lodash-es'
import { Alert, Stack } from '@mui/material'
import { RichTreeViewPro, TreeViewBaseItem, useTreeViewApiRef } from '@mui/x-tree-view-pro'
import { useElementLabel, useProjectFiles } from '@editorHooks'
import AddFolderModal from '../AddFolderModal'
import { DocumentTreeProvider } from './DocumentTreeContext'
import CustomTreeItem from './components/CustomTreeItem'

type CustomTreeViewItem = TreeViewBaseItem & {
  onCreateFolder?: () => void
  onUploadDocument?: () => void
  isItemReorderable?: boolean
  itemType?: 'folder' | 'file' | 'position' | 'pdf' | 'toc' | 'cover-page'
  level?: string
  onContext?: () => void
  disableDelete?: boolean
  isTemplate?: boolean
  isGeneratingTemplate?: boolean
  disableActions?: boolean
  item: FileItem | SectionItem
}

interface Props {
  selectedDocument: string | null
  onSelectDocument: (selection: string | null) => void
  selectedDocumentPages: number
  onSave: (ordering: FileOrder[]) => void
  generatingTemplates: Record<string, 'loading' | 'loaded' | 'error'>
  onClickShowFullDocuments?: () => void
  onUploadDocument: (parentGuid: string) => void
  disableActions: boolean
}

const FileOrdering = ({
  selectedDocument,
  onSelectDocument,
  onSave,
  generatingTemplates,
  onUploadDocument,
  disableActions,
}: Props): ReactElement => {
  const getLabel = useElementLabel()

  const { data } = useProjectFiles(documentationRootGuid)

  const documentToTreeItem = useCallback<
    (document: FileItem, level: string[], children: CustomTreeViewItem[]) => CustomTreeViewItem
  >(
    (document: FileItem, level: string[], children: CustomTreeViewItem[]) => {
      // Files uploaded for an element are markdown pages with the file attached
      const isElementFile = !!document.element_guid && !!document.attachment
      let itemType: CustomTreeViewItem['itemType'] = 'file'
      if (isElementFile || (document.element_guid && isUndefined(document.template_key))) {
        itemType = 'position'
      } else if (document.template_key === 'toc') {
        itemType = 'toc'
      } else if (document.template_key === 'cover-page') {
        itemType = 'cover-page'
      }

      let documentLabel = document.name
      if (isElementFile || (document.element_guid && isUndefined(document.template_key))) {
        documentLabel = `${document.name} (${getLabel(document.element_guid as string)})`
      }

      return {
        id: document.guid,
        label: documentLabel,
        children,
        itemType,
        expanded: false,

        level: level.join(' - '),
        onContext: () => {
          onSelectDocument(document.guid)
        },
        isTemplate: document.template_key !== null,
        isGeneratingTemplate: generatingTemplates[document.guid] === 'loading',
        disableActions,
        item: document,
      }
    },
    [getLabel, onSelectDocument, generatingTemplates, disableActions],
  )

  const documentsDocumentationItems = useMemo(
    () =>
      data.files.map(
        document =>
          ({
            ...document,
            type: 'file',
          } as FileItem & { type: 'file' }),
      ),
    [data.files],
  )

  const foldersDocumentationItems = useMemo(
    () =>
      data.sections.map(
        folder =>
          ({
            ...folder,
            type: 'folder',
          } as SectionItem & { type: 'folder' }),
      ),
    [data.sections],
  )

  const allDocumentationItems: (
    | (FileItem & { type: 'file' })
    | (SectionItem & { type: 'folder' })
  )[] = useMemo(
    () => [...documentsDocumentationItems, ...foldersDocumentationItems],
    [documentsDocumentationItems, foldersDocumentationItems],
  )

  const items: CustomTreeViewItem[] = useMemo(() => {
    // Helper function to build folder tree recursively
    const buildFolderTree = (parentGuid: string, folderLevel: string[]): CustomTreeViewItem[] => {
      const itemsInThisHierarchy: (
        | (FileItem & { type: 'file' })
        | (SectionItem & { type: 'folder' })
      )[] = allDocumentationItems.filter(item => item.parent_guid === parentGuid)

      const sortedItemsInThisHierarchy = sortBy(itemsInThisHierarchy, ['hierarchy_position'])

      let itemIndex = 0

      return sortedItemsInThisHierarchy.map(item => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const folderconfig = {} as any

        itemIndex = folderconfig?.page_number || itemIndex + 1

        let newLevel = itemIndex.toString()

        if (folderconfig?.page_prefix) {
          newLevel = `${folderconfig.page_prefix}${newLevel}`
        }

        if (folderconfig?.page_suffix) {
          newLevel = `${newLevel}${folderconfig.page_suffix}`
        }

        const newFolderLevel = [...folderLevel, newLevel]

        if (item.type === 'file') {
          const documentItem = find(data.files, { guid: item.guid })!
          const children = buildFolderTree(documentItem.guid, newFolderLevel)
          return documentToTreeItem(documentItem, newFolderLevel, children)
        } else if (item.type === 'folder') {
          const folderItem = find(data.sections, { guid: item.guid })!
          const children = buildFolderTree(folderItem.guid, newFolderLevel)

          const treeItem: CustomTreeViewItem = {
            id: folderItem.guid,
            label: folderItem.name,
            children,
            isItemReorderable: true,
            itemType: 'folder',
            onCreateFolder: () => setFolderModalOpen(folderItem.guid as string),
            onUploadDocument: () => onUploadDocument(folderItem.guid as string),
            level: newFolderLevel.join(' - '),
            onContext: () => {
              onSelectDocument(folderItem.guid)
            },
            disableDelete: false,
            isTemplate: !!folderItem.template_key,
            isGeneratingTemplate: generatingTemplates[folderItem.guid] === 'loading',
            disableActions,
            item: folderItem,
          }

          return treeItem
        } else {
          throw new Error('Invalid item type')
        }
      })
    }

    // Build the folder hierarchy starting from root folders (those without parent_guid)
    const rootFolders: CustomTreeViewItem[] = buildFolderTree(documentationRootGuid, [])

    // Return the complete tree structure
    return rootFolders
  }, [
    allDocumentationItems,
    data.files,
    data.sections,
    documentToTreeItem,
    onSelectDocument,
    onUploadDocument,
    generatingTemplates,
    disableActions,
  ])

  const [expandedItems, setExpandedItems] = useState<string[]>([])

  // MUTATIONS

  const [folderModalOpen, setFolderModalOpen] = useState<string | null>(null)

  const apiRef = useTreeViewApiRef()

  if (
    (data.files.length === 0 && data.sections.length === 0) ||
    (data.files.length === 0 && data.sections.length === 1)
  ) {
    return (
      <Stack p={1}>
        <Alert severity="info">Keine Dokumente gefunden</Alert>
      </Stack>
    )
  }

  return (
    <DocumentTreeProvider
      rootGuid={documentationRootGuid}
      selectedDocument={selectedDocument}
      disableActions={disableActions}
    >
      <Stack spacing={2}>
        <RichTreeViewPro
          data-cy="document-structure-tree"
          apiRef={apiRef}
          items={items}
          itemsReordering
          slots={{ item: CustomTreeItem }}
          expandedItems={expandedItems}
          onExpandedItemsChange={(_, itemIds) => setExpandedItems(itemIds)}
          multiSelect={false}
          selectedItems={selectedDocument}
          onSelectedItemsChange={(event, id: string | null) => {
            if (id) {
              onSelectDocument(id)
            } else {
              onSelectDocument(null)
            }
          }}
          canMoveItemToNewPosition={() => !disableActions}
          onItemPositionChange={() => {
            const newItemTree = apiRef.current!.getItemTree() as TreeViewBaseItem[]

            const positionFromTreeItems = (
              parentGuid: string | null,
              items: TreeViewBaseItem[],
            ): FileOrder[] => {
              return flatten(
                items.map((item, index) => [
                  {
                    guid: item.id,
                    hierarchy_position: index,
                    parent_guid: parentGuid || documentationRootGuid,
                  },
                  ...(item.children ? positionFromTreeItems(item.id, item.children) : []),
                ]),
              )
            }

            const ordering: FileOrder[] = positionFromTreeItems(null, newItemTree)

            onSave(ordering)
          }}
        />

        <AddFolderModal
          open={!!folderModalOpen}
          onClose={() => setFolderModalOpen(null)}
          parentGuid={folderModalOpen as string}
          onCreateFolder={folderId => {
            onSelectDocument(folderId)
          }}
        />
      </Stack>
    </DocumentTreeProvider>
  )
}

export default FileOrdering
